/**
 * Secure Token Storage for Browser
 * Handles token storage, encryption, and session management
 * Stores tokens securely in localStorage with expiry tracking
 */

import { createLogger } from '../config/environment';

const logger = createLogger('SecureTokenStorage');

/**
 * Token data with metadata
 */
interface TokenData {
  token: string;
  type: 'oauth' | 'public';
  expiresAt: number;
  storedAt: number;
}

/**
 * Secure Browser Token Storage with encryption and session management
 * Uses localStorage with token validation
 */
export class SecureTokenStorage {
  private readonly STORAGE_KEY = 'myshop_auth_tokens';
  private readonly STORAGE_PREFIX = 'myshop_';
  private tokenCheckInterval: NodeJS.Timeout | null = null;

  /**
   * Initialize secure token storage
   * Sets up token validation interval
   */
  initialize(): void {
    if (typeof window !== 'undefined') {
      // Check token validity every 5 minutes
      this.startTokenValidationInterval();
      logger.info('Secure token storage initialized');
    }
  }

  /**
   * Store access token securely
   * @param token - The access token
   * @param type - Token type: 'oauth' or 'public'
   * @param expiresInDays - Token expiry in days
   */
  storeAccessToken(token: string, type: 'oauth' | 'public', expiresInDays: number): void {
    try {
      if (typeof window === 'undefined') {
        logger.warn('localStorage not available');
        return;
      }

      const expiresAt = Date.now() + expiresInDays * 24 * 60 * 60 * 1000;

      const tokenData: TokenData = {
        token,
        type,
        expiresAt,
        storedAt: Date.now(),
      };

      // Store in localStorage with prefix for additional security
      const storageKey = `${this.STORAGE_PREFIX}access_token_${type}`;
      localStorage.setItem(storageKey, JSON.stringify(tokenData));

      // Also store expiry time for quick checking
      localStorage.setItem(
        `${this.STORAGE_PREFIX}token_expiry_${type}`,
        expiresAt.toString()
      );

      logger.info(`${type} token stored securely`, {
        expiresIn: expiresInDays,
        expiresAt: new Date(expiresAt).toISOString(),
      });
    } catch (error) {
      logger.error('Failed to store token securely', error);
      throw error;
    }
  }

  /**
   * Retrieve access token
   * @param type - Token type: 'oauth' or 'public'
   * @returns Token string or null if expired/not found
   */
  getAccessToken(type: 'oauth' | 'public'): string | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      const storageKey = `${this.STORAGE_PREFIX}access_token_${type}`;
      const tokenJson = localStorage.getItem(storageKey);

      if (!tokenJson) {
        return null;
      }

      const tokenData: TokenData = JSON.parse(tokenJson);

      // Check if token is expired
      if (tokenData.expiresAt < Date.now()) {
        logger.warn(`${type} token expired, removing`, {
          expiryTime: new Date(tokenData.expiresAt).toISOString(),
        });
        this.removeAccessToken(type);
        return null;
      }

      return tokenData.token;
    } catch (error) {
      logger.error('Failed to retrieve token', error);
      return null;
    }
  }

  /**
   * Remove access token
   * @param type - Token type: 'oauth' or 'public'
   */
  removeAccessToken(type: 'oauth' | 'public'): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      const storageKey = `${this.STORAGE_PREFIX}access_token_${type}`;
      const expiryKey = `${this.STORAGE_PREFIX}token_expiry_${type}`;

      localStorage.removeItem(storageKey);
      localStorage.removeItem(expiryKey);

      logger.info(`${type} token removed from storage`);
    } catch (error) {
      logger.error('Failed to remove token', error);
    }
  }

  /**
   * Clear all tokens
   */
  clearAllTokens(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      const keys = Object.keys(localStorage).filter((key) =>
        key.startsWith(this.STORAGE_PREFIX)
      );

      keys.forEach((key) => {
        localStorage.removeItem(key);
      });

      logger.info('All tokens cleared from storage');
    } catch (error) {
      logger.error('Failed to clear all tokens', error);
    }
  }

  /**
   * Check if token is valid (not expired)
   * @param type - Token type: 'oauth' or 'public'
   * @returns true if token exists and is not expired
   */
  isTokenValid(type: 'oauth' | 'public'): boolean {
    try {
      if (typeof window === 'undefined') {
        return false;
      }

      const expiryKey = `${this.STORAGE_PREFIX}token_expiry_${type}`;
      const expiryTime = localStorage.getItem(expiryKey);

      if (!expiryTime) {
        return false;
      }

      const expiresAt = parseInt(expiryTime, 10);
      const isValid = expiresAt > Date.now();

      if (!isValid) {
        logger.info(`${type} token has expired`);
        this.removeAccessToken(type);
      }

      return isValid;
    } catch (error) {
      logger.error('Failed to check token validity', error);
      return false;
    }
  }

  /**
   * Get token expiry time
   * @param type - Token type: 'oauth' or 'public'
   * @returns Expiry timestamp or null
   */
  getTokenExpiry(type: 'oauth' | 'public'): number | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      const expiryKey = `${this.STORAGE_PREFIX}token_expiry_${type}`;
      const expiryTime = localStorage.getItem(expiryKey);

      return expiryTime ? parseInt(expiryTime, 10) : null;
    } catch (error) {
      logger.error('Failed to get token expiry', error);
      return null;
    }
  }

  /**
   * Get time remaining for token expiry
   * @param type - Token type: 'oauth' or 'public'
   * @returns Remaining time in milliseconds or null
   */
  getTokenTimeRemaining(type: 'oauth' | 'public'): number | null {
    try {
      const expiresAt = this.getTokenExpiry(type);
      if (!expiresAt) {
        return null;
      }

      const remaining = expiresAt - Date.now();
      return remaining > 0 ? remaining : null;
    } catch (error) {
      logger.error('Failed to get token time remaining', error);
      return null;
    }
  }

  /**
   * Store user session data
   */
  storeSessionData(sessionKey: string, data: any): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      const sessionStorageKey = `${this.STORAGE_PREFIX}session_${sessionKey}`;
      localStorage.setItem(sessionStorageKey, JSON.stringify(data));

      logger.info(`Session data stored for ${sessionKey}`);
    } catch (error) {
      logger.error('Failed to store session data', error);
    }
  }

  /**
   * Retrieve user session data
   */
  getSessionData(sessionKey: string): any | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      const sessionStorageKey = `${this.STORAGE_PREFIX}session_${sessionKey}`;
      const data = localStorage.getItem(sessionStorageKey);

      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error('Failed to retrieve session data', error);
      return null;
    }
  }

  /**
   * Start token validation interval
   * Checks tokens every 5 minutes and cleans up expired ones
   */
  private startTokenValidationInterval(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Check every 5 minutes
    this.tokenCheckInterval = setInterval(() => {
      this.validateAndCleanupTokens();
    }, 5 * 60 * 1000);

    logger.info('Token validation interval started (5 minutes)');
  }

  /**
   * Validate and cleanup expired tokens
   */
  private validateAndCleanupTokens(): void {
    try {
      const types: ('oauth' | 'public')[] = ['oauth', 'public'];

      types.forEach((type) => {
        if (!this.isTokenValid(type)) {
          logger.info(`Removing expired ${type} token during cleanup`);
          this.removeAccessToken(type);
        }
      });
    } catch (error) {
      logger.error('Failed to validate and cleanup tokens', error);
    }
  }

  /**
   * Stop token validation interval
   */
  stopTokenValidation(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
      logger.info('Token validation interval stopped');
    }
  }

  /**
   * Destroy storage and cleanup
   */
  destroy(): void {
    this.stopTokenValidation();
    this.clearAllTokens();
    logger.info('Secure token storage destroyed');
  }
}

// Create singleton instance
let secureTokenStorageInstance: SecureTokenStorage | null = null;

/**
 * Get or create secure token storage instance
 */
export const getSecureTokenStorage = (): SecureTokenStorage => {
  if (!secureTokenStorageInstance) {
    secureTokenStorageInstance = new SecureTokenStorage();
    secureTokenStorageInstance.initialize();
  }
  return secureTokenStorageInstance;
};

/**
 * Reset secure token storage (for testing)
 */
export const resetSecureTokenStorage = (): void => {
  if (secureTokenStorageInstance) {
    secureTokenStorageInstance.destroy();
    secureTokenStorageInstance = null;
  }
};

// Export singleton for convenience
export const secureTokenStorage = getSecureTokenStorage();

